import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceGrowthExperimentBibleApi,
  type AttributionFactor,
  type ConfidenceFactor,
  type DecisionQuestion,
  type ExperimentLibraryEntry,
  type ExperimentSpecField,
  type GrowthBibleDashboard,
  type GrowthBibleStatus,
  type HypothesisTemplate,
  type LearningCaptureItem,
  type LifecycleStage,
  type OpportunityArea,
  type PhilosophyAction,
  type RecommendationEvaluation,
  type RecommendationField,
  type RootCauseFactor,
} from '../../api/marketplace-growth-experiment-bible';

type Tab = 'overview' | 'lifecycle' | 'opportunities' | 'hypothesis' | 'recommendations' | 'experiments' | 'attribution' | 'learning' | 'status';
type Theme = 'light' | 'dark';

const SAMPLE_REC_FIELDS: Record<string, string> = {
  business_objective: 'Improve listing CTR and conversion',
  reason: 'Main image lacks usage context vs top competitors',
  supporting_evidence: 'Comparable ASINs with lifestyle imagery show 18% higher CTR',
  expected_kpi_improvement: 'CTR +12%, CVR +5%',
  expected_revenue_impact: '$4,200/month',
  confidence_score: '0.78',
};

export function MarketplaceGrowthExperimentBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<GrowthBibleDashboard | null>(null);
  const [philosophy, setPhilosophy] = useState<PhilosophyAction[]>([]);
  const [lifecycle, setLifecycle] = useState<LifecycleStage[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityArea[]>([]);
  const [rootCauses, setRootCauses] = useState<RootCauseFactor[]>([]);
  const [hypothesis, setHypothesis] = useState<HypothesisTemplate | null>(null);
  const [recFields, setRecFields] = useState<RecommendationField[]>([]);
  const [confidence, setConfidence] = useState<ConfidenceFactor[]>([]);
  const [expFields, setExpFields] = useState<ExperimentSpecField[]>([]);
  const [attribution, setAttribution] = useState<AttributionFactor[]>([]);
  const [learning, setLearning] = useState<LearningCaptureItem[]>([]);
  const [expLibrary, setExpLibrary] = useState<ExperimentLibraryEntry[]>([]);
  const [decisions, setDecisions] = useState<DecisionQuestion[]>([]);
  const [status, setStatus] = useState<GrowthBibleStatus | null>(null);
  const [evalResult, setEvalResult] = useState<RecommendationEvaluation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, phil, lc, opp, rc, hyp, rec, conf, exp, attr, learn, lib, qs, st] = await Promise.all([
        marketplaceGrowthExperimentBibleApi.dashboard(),
        marketplaceGrowthExperimentBibleApi.philosophy(),
        marketplaceGrowthExperimentBibleApi.lifecycle(),
        marketplaceGrowthExperimentBibleApi.opportunities(),
        marketplaceGrowthExperimentBibleApi.rootCauses(),
        marketplaceGrowthExperimentBibleApi.hypothesisTemplate(),
        marketplaceGrowthExperimentBibleApi.recommendationSpec(),
        marketplaceGrowthExperimentBibleApi.confidenceFactors(),
        marketplaceGrowthExperimentBibleApi.experimentSpec(),
        marketplaceGrowthExperimentBibleApi.attribution(),
        marketplaceGrowthExperimentBibleApi.learningCapture(),
        marketplaceGrowthExperimentBibleApi.experimentLibrary(),
        marketplaceGrowthExperimentBibleApi.decisionQuestions(),
        marketplaceGrowthExperimentBibleApi.status(),
      ]);
      setDashboard(dash);
      setPhilosophy(phil);
      setLifecycle(lc);
      setOpportunities(opp);
      setRootCauses(rc);
      setHypothesis(hyp);
      setRecFields(rec);
      setConfidence(conf);
      setExpFields(exp);
      setAttribution(attr);
      setLearning(learn);
      setExpLibrary(lib);
      setDecisions(qs);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load growth experiment bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const evaluateRecommendation = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceGrowthExperimentBibleApi.evaluateRecommendation({
        recommendation_id: 'rec-demo-001',
        fields: SAMPLE_REC_FIELDS,
      });
      setEvalResult(result);
      setActionMsg(result.valid ? 'Recommendation passes MIB-000004 validation — experiment-ready' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Evaluation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';
  const accent = 'text-indigo-500';
  const tabActive = 'bg-indigo-600 text-white';
  const tabInactive = `${card} ${muted}`;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'lifecycle', label: 'Decision Lifecycle' },
    { id: 'opportunities', label: 'Opportunities & RCA' },
    { id: 'hypothesis', label: 'Hypothesis & Confidence' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'experiments', label: 'Experiment Library' },
    { id: 'attribution', label: 'Attribution & Learning' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>MIB-000004</p>
          <h1 className="text-2xl font-semibold">Marketplace Growth Experiment & Decision Intelligence Bible</h1>
          <p className={`text-sm ${muted}`}>
            Scientific optimization framework, recommendation engine, confidence scoring, attribution and continuous learning
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-bible" className={`${accent} hover:underline`}>MIB-000001</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-kpi-bible" className={`${accent} hover:underline`}>MIB-000002</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-dna-bible" className={`${accent} hover:underline`}>MIB-000003</Link>
            <span className={muted}>·</span>
            <Link to="/decision/recommendations" className={`${accent} hover:underline`}>Recommendation Center</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : tabInactive}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Bible ID', value: dashboard.bible_id },
              { label: 'Lifecycle Stages', value: dashboard.lifecycle_stages },
              { label: 'Opportunity Areas', value: dashboard.opportunity_areas },
              { label: 'Experiment Library', value: dashboard.experiment_library },
              { label: 'Root Cause Factors', value: dashboard.root_cause_factors },
              { label: 'Confidence Factors', value: dashboard.confidence_factors },
              { label: 'Active Experiments', value: dashboard.active_experiments },
              { label: 'Open Recommendations', value: dashboard.open_recommendations },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Product Philosophy — Observe → Learn → Improve</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {philosophy.map((p) => (
                <span key={p.key} className="rounded-full bg-indigo-100 px-3 py-1 text-xs dark:bg-indigo-900" title={p.description}>{p.name}</span>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Decision Intelligence Questions</h3>
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm">
              {decisions.map((q) => (
                <li key={q.key}>{q.question}</li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {tab === 'lifecycle' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">16-Stage Decision Intelligence Lifecycle</h3>
            <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
              {lifecycle.map((s) => (
                <div key={s.key} className="rounded-lg border border-indigo-200 p-3 dark:border-indigo-800">
                  <span className="text-xs font-mono text-indigo-500">{s.order}</span>
                  <p className="font-medium">{s.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Opportunity Detection Areas</h3>
            <div className="mt-3 space-y-2">
              {opportunities.map((o) => (
                <div key={o.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{o.name}</span>
                    {o.priority && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs dark:bg-amber-900">{o.priority}</span>}
                  </div>
                  {o.description && <p className={`mt-1 text-sm ${muted}`}>{o.description}</p>}
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Root Cause Analysis — Ranked Factors</h3>
            <div className="mt-3 space-y-2 max-h-[600px] overflow-y-auto">
              {rootCauses.map((r) => (
                <div key={r.key} className="flex items-center justify-between rounded-lg border p-2 dark:border-slate-700">
                  <div>
                    <span className="font-medium">{r.name}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{r.category}</span>
                  </div>
                  {r.probability != null && (
                    <span className="font-mono text-sm text-indigo-500">{(r.probability * 100).toFixed(0)}%</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'hypothesis' && hypothesis && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Hypothesis Engine — Required Fields</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
              {hypothesis.required_fields.map((f) => (
                <li key={f}>{f.replace(/_/g, ' ')}</li>
              ))}
            </ul>
            {hypothesis.example && (
              <p className={`mt-4 rounded-lg bg-indigo-50 p-3 text-sm italic dark:bg-indigo-950 ${muted}`}>{hypothesis.example}</p>
            )}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Confidence Engine — Weighted Factors</h3>
            <div className="mt-3 space-y-2">
              {confidence.map((c) => (
                <div key={c.key} className="flex items-center justify-between rounded-lg border p-2 dark:border-slate-700">
                  <div>
                    <span className="font-medium">{c.name}</span>
                    {c.description && <p className={`text-xs ${muted}`}>{c.description}</p>}
                  </div>
                  <span className="font-mono text-sm">{(c.weight * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Recommendation Specification — 14 Required Fields</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {recFields.map((f) => (
                <div key={f.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{f.name}</span>
                    {f.required && <span className="rounded bg-red-100 px-1.5 text-xs text-red-700 dark:bg-red-900 dark:text-red-300">required</span>}
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Evaluate Sample Recommendation</h3>
            <p className={`mt-1 text-sm ${muted}`}>Validates measurable intent, evidence, explainability and experiment readiness per MIB §15.</p>
            <button type="button" disabled={busy} onClick={evaluateRecommendation} className="mt-3 rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50">
              {busy ? 'Evaluating…' : 'Evaluate Demo Recommendation'}
            </button>
            {evalResult && (
              <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: 'Valid', value: evalResult.valid ? 'Yes' : 'No' },
                  { label: 'Measurable Intent', value: evalResult.has_measurable_intent ? 'Yes' : 'No' },
                  { label: 'Evidence Based', value: evalResult.evidence_based ? 'Yes' : 'No' },
                  { label: 'Experiment Ready', value: evalResult.experiment_ready ? 'Yes' : 'No' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border p-3 dark:border-slate-700">
                    <p className={`text-xs ${muted}`}>{item.label}</p>
                    <p className="font-semibold">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'experiments' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Growth Experiment Specification</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {expFields.map((f) => (
                <div key={f.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{f.name}</span>
                  <p className={`mt-1 text-sm ${muted}`}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Experiment Library — Reusable Templates</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {expLibrary.map((e) => (
                <div key={e.key} className="rounded-lg border p-4 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{e.name}</span>
                    <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{e.category}</span>
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{e.description}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {e.target_kpis.map((k) => (
                      <span key={k} className="rounded bg-slate-100 px-2 py-0.5 text-xs font-mono dark:bg-slate-800">{k}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'attribution' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Attribution Engine — Factor Contributions</h3>
            <div className="mt-3 space-y-2">
              {attribution.map((a) => (
                <div key={a.key} className="flex items-center justify-between rounded-lg border p-2 dark:border-slate-700">
                  <span>{a.name}</span>
                  {a.contribution_pct != null && (
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 rounded bg-slate-200 dark:bg-slate-700">
                        <div className="h-2 rounded bg-indigo-500" style={{ width: `${a.contribution_pct}%` }} />
                      </div>
                      <span className="font-mono text-sm">{a.contribution_pct}%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Continuous Learning Capture</h3>
            <div className="mt-3 space-y-2">
              {learning.map((l) => (
                <div key={l.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{l.name}</span>
                  <p className={`mt-1 text-sm ${muted}`}>{l.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">MIB-000004 Bible Status</h3>
          <dl className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(status).map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
